'use client';

import { FC, useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { Rating } from 'hotdesk_typescript';
import { useLocale, useTranslations } from 'next-intl';
import ReviewCard from './ReviewCard';
import HotdeskButton from '@/hotdesk-ui/components/shared/HotdeskButton';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { loadWebReviewsPaginationLimit } from '@/hotdesk-ui/helpers/web-reviews-display';
import { parseData } from '@/hotdesk-ui/helpers/utils';
import { RatingService } from '@/hotdesk-ui/services/rating.service';
import { tracking } from '@/hotdesk-ui/services/tracking.service';
import styles from './ReviewsSection.module.scss';
const SHOW_ALL_THRESHOLD = 3;
export interface ReviewsSectionProps {
  /** First page of published reviews, fetched on the server. */
  reviews: Rating[];
  /** API total of published reviews; more pages load on demand. */
  reviewsTotal?: number;
  overAllRating: number;
  ratingCount: number;
  /** listing_details | workspace_details | … */
  trackingSource?: string;
  listingId?: number;
  workspaceId?: number;
}
const ReviewsSection: FC<ReviewsSectionProps> = ({
  reviews,
  reviewsTotal,
  overAllRating,
  ratingCount,
  trackingSource = 'listing_details',
  listingId,
  workspaceId
}) => {
  const t = useTranslations('workspace-details');
  const locale = useLocale();
  const [showAll, setShowAll] = useState(false);
  const [pageLimit, setPageLimit] = useState(1);
  const [loadedReviews, setLoadedReviews] = useState<Rating[]>(reviews);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    setLoadedReviews(reviews);
  }, [reviews]);
  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const apiConfig = await new ApiConfigration().getApiConfig(locale);
      const res = await new RatingService(apiConfig).getPublishedReviews({
        listingId,
        workspaceId,
        offset: loadedReviews.length
      });
      const next = parseData(res.results) as Rating[];
      setLoadedReviews(prev => {
        const seen = new Set(prev.map(r => r.id));
        return [...prev, ...next.filter(r => !seen.has(r.id))];
      });
    } catch (error) {
      console.error('Failed to load more reviews', error);
    } finally {
      setLoadingMore(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    loadWebReviewsPaginationLimit().then(limit => {
      if (!cancelled) setPageLimit(limit);
    }).catch(() => {
      if (!cancelled) setPageLimit(1);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (!reviews || reviews.length === 0) return;
    const screenUrl = typeof window !== 'undefined' ? window.location.href : undefined;
    if (trackingSource.startsWith('workspace')) {
      tracking.workspaceReviewsViewed({
        workspaceId,
        source: trackingSource,
        screenUrl
      });
    } else {
      tracking.listingReviewsViewed({
        listingId,
        source: trackingSource,
        screenUrl
      });
    }
  }, [reviews?.length, trackingSource, listingId, workspaceId]);
  if (!reviews || reviews.length === 0) return null;
  const displayRating = overAllRating > 0 ? (overAllRating / 100 * 5).toFixed(1) : '0';
  // Always show at least SHOW_ALL_THRESHOLD reviews; the
  // "Read All Reviews" toggle only appears beyond that.
  const initialVisible = Math.max(SHOW_ALL_THRESHOLD, pageLimit);
  // The server only sends the first page; `reviewsTotal` says how many exist.
  const total = Math.max(reviewsTotal ?? 0, loadedReviews.length);
  const hasMore = loadedReviews.length < total;
  const visibleReviews = showAll ? loadedReviews : loadedReviews.slice(0, initialVisible);
  return <section className={styles.section} data-sentry-component="ReviewsSection" data-sentry-source-file="ReviewsSection.tsx">
            <div className={styles.header}>
                <h2 className={styles.heading}>{t('reviews')}</h2>
                {overAllRating > 0 && <div className={styles.ratingBadge}>
                        <Star size={16} fill="#000" color="#000" />
                        <span className={styles.ratingValue}>
                            {displayRating}
                            {ratingCount > 0 && ` (${ratingCount})`}
                        </span>
                    </div>}
            </div>
            <div className={styles.grid}>
                {visibleReviews.map((review, i) => <ReviewCard key={review.id || i} review={review} />)}
            </div>
            {total > initialVisible && <div className={styles.readAllWrap}>
                    {showAll && hasMore && <HotdeskButton variant="primary" size="md" onClick={loadMore} disabled={loadingMore}>
                            {t('loadMoreReviews')}
                        </HotdeskButton>}
                    <HotdeskButton variant="secondary" size="md" onClick={() => setShowAll(!showAll)}>
                        {showAll ? t('showLess') : t('readAllReviews')}
                    </HotdeskButton>
                </div>}
        </section>;
};
export default ReviewsSection;